import React, { useState } from 'react';
import { Icon, Button, Input, Modal, Avatar, Badge, EmptyState } from '../components/UI';
import { ProgrammeCard } from '../components/Cards';
import { useApp } from '../context/AppContext';

const PROG_ICONS = ['book', 'tool', 'briefcase', 'cpu', 'recycle', 'award', 'chart', 'users', 'inbox', 'settings'];
const PROG_COLORS = ['#2E8B57', '#2F6FB0', '#EC7D2A', '#7C3AED', '#E11D48', '#0891B2', '#CA8A04', '#374151'];

// ── ADMIN HOME ────────────────────────────────────────────────────────────────
export function AdminHome({ setActiveTab }) {
  const { data } = useApp();
  const students = data.students.filter(s => s.role === 'student');
  const totalCourses = data.programmes.reduce((a, p) => a + p.courses.length, 0);
  const totalEnrolments = students.reduce((a, s) => a + s.programmes.length, 0);

  return (
    <div className="page-enter" style={{ maxWidth: 1280, margin: '0 auto', padding: '44px 28px' }}>
      <div style={{ marginBottom: 44 }}>
        <p style={{ fontSize: 11, fontWeight: 800, color: 'var(--orange)', letterSpacing: '0.14em', textTransform: 'uppercase', margin: '0 0 10px', fontFamily: 'var(--font-heading)' }}>Admin Portal</p>
        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 38, fontWeight: 800, color: 'var(--dark)', margin: '0 0 12px', letterSpacing: '-0.03em' }}>AlgoAtWork</h1>
        <p style={{ fontSize: 16, color: 'var(--dark-60)', margin: 0 }}>Manage programmes, students, and course content from one place.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 44 }}>
        {[
          { label: 'Programmes', value: data.programmes.length, color: 'var(--orange)', icon: 'book', tab: 'Programmes' },
          { label: 'Students', value: students.length, color: 'var(--info)', icon: 'users', tab: 'Students' },
          { label: 'Courses', value: totalCourses, color: 'var(--purple)', icon: 'award', tab: 'Programmes' },
          { label: 'Enrolments', value: totalEnrolments, color: 'var(--success)', icon: 'chart', tab: 'Stats' },
        ].map(s => (
          <div key={s.label} onClick={() => setActiveTab(s.tab)} style={{ background: 'var(--white)', border: '1.5px solid var(--dark-10)', borderRadius: 'var(--radius-lg)', padding: '22px 24px', cursor: 'pointer', transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
            <div style={{ fontSize: 36, fontWeight: 800, color: s.color, lineHeight: 1, fontFamily: 'var(--font-heading)', marginBottom: 6 }}>{s.value}</div>
            <div style={{ fontSize: 13, color: 'var(--dark-60)' }}>{s.label}</div>
          </div>
        ))}
      </div>

      <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 22, fontWeight: 800, color: 'var(--dark)', margin: '0 0 20px', letterSpacing: '-0.01em' }}>All Programmes</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px,1fr))', gap: 20 }}>
        {data.programmes.map(p => <ProgrammeCard key={p.id} programme={p} />)}
      </div>
    </div>
  );
}

// ── ADMIN PROGRAMMES ──────────────────────────────────────────────────────────
export function AdminProgrammes() {
  const { data, createProgramme: ctxCreateProg, deleteProgramme: ctxDeleteProg, addCourse: ctxAddCourse, updateCourse: ctxUpdateCourse, deleteCourse: ctxDeleteCourse, addAuditLog } = useApp();
  const [modal, setModal] = useState(null);
  const [selectedProg, setSelectedProg] = useState(null);
  const [form, setForm] = useState({});

  const f = (key) => v => setForm(prev => ({ ...prev, [key]: v }));

  const openCreateProg = () => { setForm({ name: '', description: '', icon: 'book', color: '#2F6FB0' }); setModal('programme'); };

  const saveProgramme = async () => {
    if (!form.name?.trim()) return;
    await ctxCreateProg(form);
    await addAuditLog('Programme created', `Created programme "${form.name.trim()}"`);
    setModal(null);
  };

  const handleDeleteProgramme = async (prog) => {
    if (window.confirm(`Delete "${prog.name}" and all its courses? This cannot be undone.`)) {
      await ctxDeleteProg(prog.id);
      await addAuditLog('Programme deleted', `Deleted programme "${prog.name}" (${prog.courses.length} course${prog.courses.length !== 1 ? 's' : ''} removed)`);
    }
  };

  const openAdd = (prog) => { setSelectedProg(prog); setForm({ name: '', description: '', category: '', link: '', studentCount: '' }); setModal('course'); };
  const openEdit = (prog, course) => { setSelectedProg(prog); setForm({ ...course }); setModal('editCourse'); };

  const saveCourse = async () => {
    await ctxAddCourse(selectedProg.id, form);
    await addAuditLog('Course added', `Added course "${form.name}" to "${selectedProg.name}"`);
    setModal(null);
  };

  const saveEdit = async () => {
    await ctxUpdateCourse(selectedProg.id, form.id, form);
    await addAuditLog('Course updated', `Updated course "${form.name}" in "${selectedProg.name}"`);
    setModal(null);
  };

  const handleDeleteCourse = async (prog, course) => {
    if (window.confirm(`Delete "${course.name}"?`)) {
      await ctxDeleteCourse(prog.id, course.id);
      await addAuditLog('Course deleted', `Deleted course "${course.name}" from "${prog.name}"`);
    }
  };

  return (
    <div className="page-enter" style={{ maxWidth: 1280, margin: '0 auto', padding: '44px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 30, fontWeight: 800, color: 'var(--dark)', margin: 0, letterSpacing: '-0.02em' }}>Manage Programmes</h1>
        <Button onClick={openCreateProg}><Icon name="plus" size={15} color="white" /> New programme</Button>
      </div>

      {data.programmes.length === 0 && (
        <EmptyState icon="book" title="No programmes yet" message="Create your first programme to get started."
          action={<Button onClick={openCreateProg}><Icon name="plus" size={14} color="white" /> Create programme</Button>} />
      )}

      {data.programmes.map(prog => (
        <div key={prog.id} style={{ marginBottom: 28, background: 'var(--white)', border: '1.5px solid var(--dark-10)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
          <div style={{ padding: '20px 26px', borderBottom: '1px solid var(--dark-10)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: prog.color + '07' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 42, height: 42, borderRadius: 11, background: prog.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name={prog.icon} size={20} color={prog.color} />
              </div>
              <div>
                <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: 'var(--dark)', margin: 0 }}>{prog.name}</h3>
                <p style={{ fontSize: 13, color: 'var(--dark-60)', margin: 0 }}>{prog.courses.length} course{prog.courses.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Button size="sm" onClick={() => openAdd(prog)}><Icon name="plus" size={14} color="white" /> Add course</Button>
              <button onClick={() => handleDeleteProgramme(prog)} title="Delete programme"
                style={{ background: 'var(--danger-soft)', border: 'none', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', color: 'var(--danger)', display: 'flex', alignItems: 'center' }}>
                <Icon name="trash" size={15} color="var(--danger)" />
              </button>
            </div>
          </div>
          <div style={{ padding: 20 }}>
            {prog.courses.length === 0 ? (
              <p style={{ color: 'var(--dark-40)', fontSize: 14, textAlign: 'center', padding: '24px 0', margin: 0 }}>No courses yet. Add the first one above.</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px,1fr))', gap: 14 }}>
                {prog.courses.map(course => (
                  <div key={course.id} style={{ border: '1.5px solid var(--dark-10)', borderRadius: 'var(--radius-lg)', padding: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                      <Badge bg={prog.color + '18'} color={prog.color}>{course.category}</Badge>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => openEdit(prog, course)} style={{ background: 'var(--dark-05)', border: 'none', borderRadius: 6, padding: '5px 7px', cursor: 'pointer', color: 'var(--dark-60)', display: 'flex' }}><Icon name="edit" size={13} /></button>
                        <button onClick={() => handleDeleteCourse(prog, course)} style={{ background: 'var(--danger-soft)', border: 'none', borderRadius: 6, padding: '5px 7px', cursor: 'pointer', color: 'var(--danger)', display: 'flex' }}><Icon name="trash" size={13} /></button>
                      </div>
                    </div>
                    <p style={{ fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 700, color: 'var(--dark)', margin: '0 0 4px' }}>{course.name}</p>
                    <p style={{ fontSize: 12, color: 'var(--dark-60)', margin: '0 0 10px', lineHeight: 1.5 }}>{course.description}</p>
                    {course.link
                      ? <a href={course.link} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--info)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="external" size={12} color="var(--info)" /> Has live link</a>
                      : <span style={{ fontSize: 12, color: 'var(--warning)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="lock" size={12} color="var(--warning)" /> No link yet</span>
                    }
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Create Programme Modal */}
      {modal === 'programme' && (
        <Modal title="Create new programme" onClose={() => setModal(null)} width={520}>
          <Input label="Programme name" value={form.name || ''} onChange={f('name')} required />
          <Input label="Description" value={form.description || ''} onChange={f('description')} />
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-heading)', fontSize: 13, fontWeight: 600, color: 'var(--dark-80)', marginBottom: 8 }}>Icon</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {PROG_ICONS.map(icon => (
                <button key={icon} onClick={() => setForm(p => ({ ...p, icon }))}
                  style={{ width: 40, height: 40, borderRadius: 10, border: `2px solid ${form.icon === icon ? form.color : 'var(--dark-20)'}`, background: form.icon === icon ? form.color + '15' : 'var(--dark-05)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                  <Icon name={icon} size={18} color={form.icon === icon ? form.color : 'var(--dark-40)'} />
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 22 }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-heading)', fontSize: 13, fontWeight: 600, color: 'var(--dark-80)', marginBottom: 8 }}>Colour</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {PROG_COLORS.map(color => (
                <button key={color} onClick={() => setForm(p => ({ ...p, color }))}
                  style={{ width: 34, height: 34, borderRadius: '50%', background: color, border: `3px solid ${form.color === color ? 'white' : 'transparent'}`, cursor: 'pointer', outline: `2px solid ${form.color === color ? color : 'transparent'}`, outlineOffset: 2, transition: 'outline 0.1s' }} />
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={saveProgramme} disabled={!form.name?.trim()}><Icon name="check" size={14} color="white" /> Create programme</Button>
          </div>
        </Modal>
      )}

      {/* Course Modal */}
      {(modal === 'course' || modal === 'editCourse') && (
        <Modal title={modal === 'course' ? `Add course — ${selectedProg?.name}` : 'Edit course'} onClose={() => setModal(null)}>
          <Input label="Course name" value={form.name || ''} onChange={f('name')} required />
          <Input label="Description" value={form.description || ''} onChange={f('description')} />
          <Input label="Category" value={form.category || ''} onChange={f('category')} placeholder="e.g. Cisco, Google, IoT" required />
          <Input label="External link (optional)" value={form.link || ''} onChange={f('link')} placeholder="https://" />
          <Input label="Student count (optional)" value={form.studentCount || ''} onChange={f('studentCount')} type="number" />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={modal === 'course' ? saveCourse : saveEdit}><Icon name="check" size={14} color="white" /> {modal === 'course' ? 'Add course' : 'Save changes'}</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── ADMIN STUDENTS ────────────────────────────────────────────────────────────
export function AdminStudents() {
  const { data, createStudent, updateStudent, removeStudent, addAuditLog } = useApp();
  const [modal, setModal] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState('');
  const [filterProg, setFilterProg] = useState(null);

  const students = data.students.filter(s => s.role === 'student');
  const filtered = students.filter(s => {
    const matchSearch = s.name.toLowerCase().includes(search.toLowerCase()) || s.email.toLowerCase().includes(search.toLowerCase());
    const matchProg = filterProg ? s.programmes.includes(filterProg) : true;
    return matchSearch && matchProg;
  });

  const f = (key) => v => setForm(prev => ({ ...prev, [key]: v }));

  const openAdd = () => { setEditTarget(null); setForm({ name: '', email: '', password: '', programmes: [] }); setModal('student'); };
  const openEdit = (s) => { setEditTarget(s); setForm({ ...s }); setModal('student'); };

  const save = async () => {
    if (editTarget) {
      await updateStudent(editTarget.id, { name: form.name, email: form.email, password: form.password, programmes: form.programmes || [] });
      await addAuditLog('Student updated', `Updated student "${form.name}"`);
    } else {
      await createStudent({ name: form.name, email: form.email, password: form.password, programmes: form.programmes || [] });
      await addAuditLog('Student added', `Added student "${form.name}" (${form.email})`);
    }
    setModal(null);
  };

  const remove = async (student) => {
    if (window.confirm(`Remove ${student.name}?`)) {
      await removeStudent(student.id);
      await addAuditLog('Student removed', `Removed student "${student.name}" (${student.email})`);
    }
  };

  const toggleProg = (progId) => {
    setForm(prev => ({ ...prev, programmes: prev.programmes.includes(progId) ? prev.programmes.filter(p => p !== progId) : [...prev.programmes, progId] }));
  };

  const activeProg = filterProg ? data.programmes.find(p => p.id === filterProg) : null;

  return (
    <div className="page-enter" style={{ maxWidth: 1280, margin: '0 auto', padding: '44px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 30, fontWeight: 800, color: 'var(--dark)', margin: '0 0 4px', letterSpacing: '-0.02em' }}>Students</h1>
          <p style={{ fontSize: 14, color: 'var(--dark-60)', margin: 0 }}>
            {filtered.length} {activeProg ? `student${filtered.length !== 1 ? 's' : ''} in ${activeProg.name}` : `of ${students.length} registered`}
          </p>
        </div>
        <Button onClick={openAdd}><Icon name="plus" size={15} color="white" /> Add student</Button>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 14 }}>
        <Icon name="search" size={16} color="var(--dark-40)" style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)' }} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email…"
          style={{ width: '100%', padding: '10px 14px 10px 40px', border: '1.5px solid var(--dark-20)', borderRadius: 'var(--radius-md)', fontSize: 14, outline: 'none', background: 'white' }}
          onFocus={e => e.target.style.borderColor = 'var(--orange)'}
          onBlur={e => e.target.style.borderColor = 'var(--dark-20)'}
        />
      </div>

      {/* Programme filter pills */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        <button onClick={() => setFilterProg(null)}
          style={{ padding: '6px 14px', borderRadius: 99, border: `1.5px solid ${!filterProg ? 'var(--dark)' : 'var(--dark-20)'}`, background: !filterProg ? 'var(--dark)' : 'transparent', color: !filterProg ? 'white' : 'var(--dark-60)', fontSize: 13, fontFamily: 'var(--font-heading)', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}>
          All
        </button>
        {data.programmes.map(prog => {
          const active = filterProg === prog.id;
          const count = students.filter(s => s.programmes.includes(prog.id)).length;
          return (
            <button key={prog.id} onClick={() => setFilterProg(active ? null : prog.id)}
              style={{ padding: '6px 14px', borderRadius: 99, border: `1.5px solid ${active ? prog.color : 'var(--dark-20)'}`, background: active ? prog.color + '15' : 'transparent', color: active ? prog.color : 'var(--dark-60)', fontSize: 13, fontFamily: 'var(--font-heading)', fontWeight: active ? 700 : 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s' }}>
              <Icon name={prog.icon} size={12} color={active ? prog.color : 'var(--dark-40)'} />
              {prog.name}
              <span style={{ background: active ? prog.color + '25' : 'var(--dark-10)', color: active ? prog.color : 'var(--dark-60)', borderRadius: 99, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div style={{ background: 'var(--white)', border: '1.5px solid var(--dark-10)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 3fr auto', padding: '12px 22px', background: 'var(--dark-05)', borderBottom: '1px solid var(--dark-10)', gap: 16 }}>
          {['Name', 'Email', 'Programmes', ''].map(h => (
            <div key={h} style={{ fontSize: 11, fontWeight: 700, color: 'var(--dark-60)', letterSpacing: '0.07em', textTransform: 'uppercase', fontFamily: 'var(--font-heading)' }}>{h}</div>
          ))}
        </div>
        {filtered.length === 0 && (
          <div style={{ padding: '40px 22px', textAlign: 'center', color: 'var(--dark-40)', fontSize: 14 }}>No students found</div>
        )}
        {filtered.map((student, i) => (
          <div key={student.id} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 3fr auto', padding: '15px 22px', borderBottom: i < filtered.length - 1 ? '1px solid var(--dark-10)' : 'none', alignItems: 'center', gap: 16, transition: 'background 0.1s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--dark-05)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Avatar name={student.name} size={34} />
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--dark)', fontFamily: 'var(--font-heading)' }}>{student.name}</span>
            </div>
            <span style={{ fontSize: 13, color: 'var(--dark-60)' }}>{student.email}</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {student.programmes.length === 0
                ? <span style={{ fontSize: 12, color: 'var(--dark-40)' }}>No programmes</span>
                : student.programmes.map(pid => {
                    const prog = data.programmes.find(p => p.id === pid);
                    return prog ? <Badge key={pid} bg={prog.color + '18'} color={prog.color}>{prog.name}</Badge> : null;
                  })}
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <button onClick={() => openEdit(student)} style={{ background: 'var(--dark-05)', border: 'none', borderRadius: 7, padding: '6px 9px', cursor: 'pointer', color: 'var(--dark-60)', display: 'flex' }}><Icon name="edit" size={14} /></button>
              <button onClick={() => remove(student)} style={{ background: 'var(--danger-soft)', border: 'none', borderRadius: 7, padding: '6px 9px', cursor: 'pointer', color: 'var(--danger)', display: 'flex' }}><Icon name="trash" size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {modal === 'student' && (
        <Modal title={editTarget ? 'Edit student' : 'Add student'} onClose={() => setModal(null)}>
          <Input label="Full name" value={form.name || ''} onChange={f('name')} required />
          <Input label="Email address" value={form.email || ''} onChange={f('email')} type="email" required />
          <Input label="Password" value={form.password || ''} onChange={f('password')} placeholder={editTarget ? 'Leave blank to keep current password' : ''} required={!editTarget} />
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-heading)', fontSize: 13, fontWeight: 600, color: 'var(--dark-80)', marginBottom: 10 }}>Assigned programmes</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {data.programmes.map(prog => {
                const checked = (form.programmes || []).includes(prog.id);
                return (
                  <div key={prog.id} onClick={() => toggleProg(prog.id)} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 13px', border: `1.5px solid ${checked ? prog.color : 'var(--dark-20)'}`, borderRadius: 'var(--radius-md)', cursor: 'pointer', background: checked ? prog.color + '0C' : 'transparent', transition: 'all 0.15s' }}>
                    <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${checked ? prog.color : 'var(--dark-20)'}`, background: checked ? prog.color : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {checked && <Icon name="check" size={11} color="white" />}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Icon name={prog.icon} size={13} color={checked ? prog.color : 'var(--dark-40)'} />
                      <span style={{ fontSize: 13, fontWeight: checked ? 700 : 400, color: checked ? prog.color : 'var(--dark-60)', fontFamily: 'var(--font-heading)' }}>{prog.name}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={save} disabled={!form.name?.trim() || !form.email?.trim() || (!editTarget && !form.password?.trim())}><Icon name="check" size={14} color="white" /> {editTarget ? 'Save changes' : 'Add student'}</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── ADMIN AUDIT LOG ───────────────────────────────────────────────────────────
function auditBadge(action) {
  const a = action.toLowerCase();
  if (a.includes('added') || a.includes('created')) return { bg: 'var(--success-soft)', color: 'var(--success)' };
  if (a.includes('removed') || a.includes('deleted')) return { bg: 'var(--danger-soft)', color: 'var(--danger)' };
  return { bg: 'var(--info-soft)', color: 'var(--info)' };
}

function fmtTs(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) + '  ·  ' + d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
}

export function AdminAuditLog() {
  const { data } = useApp();
  const [search, setSearch] = useState('');

  const entries = (data.auditLog || []).filter(e =>
    !search || [e.action, e.details, e.actor].some(s => s.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="page-enter" style={{ maxWidth: 1280, margin: '0 auto', padding: '44px 28px' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 30, fontWeight: 800, color: 'var(--dark)', margin: '0 0 4px', letterSpacing: '-0.02em' }}>Audit Log</h1>
        <p style={{ fontSize: 14, color: 'var(--dark-60)', margin: 0 }}>{data.auditLog?.length || 0} events recorded</p>
      </div>

      <div style={{ position: 'relative', marginBottom: 20 }}>
        <Icon name="search" size={16} color="var(--dark-40)" style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)' }} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search events, actions, or actors…"
          style={{ width: '100%', padding: '10px 14px 10px 40px', border: '1.5px solid var(--dark-20)', borderRadius: 'var(--radius-md)', fontSize: 14, outline: 'none', background: 'white' }}
          onFocus={e => e.target.style.borderColor = 'var(--orange)'}
          onBlur={e => e.target.style.borderColor = 'var(--dark-20)'}
        />
      </div>

      {entries.length === 0 ? (
        <EmptyState icon="eye" title="No events yet"
          message={search ? 'No events match your search.' : 'Admin actions — adding students, creating programmes — will appear here.'} />
      ) : (
        <div style={{ background: 'var(--white)', border: '1.5px solid var(--dark-10)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '210px 150px 1fr', padding: '12px 22px', background: 'var(--dark-05)', borderBottom: '1px solid var(--dark-10)', gap: 16 }}>
            {['Timestamp', 'Action', 'Details'].map(h => (
              <div key={h} style={{ fontSize: 11, fontWeight: 700, color: 'var(--dark-60)', letterSpacing: '0.07em', textTransform: 'uppercase', fontFamily: 'var(--font-heading)' }}>{h}</div>
            ))}
          </div>
          {entries.map((entry, i) => {
            const badge = auditBadge(entry.action);
            return (
              <div key={entry.id}
                style={{ display: 'grid', gridTemplateColumns: '210px 150px 1fr', padding: '14px 22px', borderBottom: i < entries.length - 1 ? '1px solid var(--dark-10)' : 'none', alignItems: 'center', gap: 16, transition: 'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--dark-05)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div>
                  <div style={{ fontSize: 13, color: 'var(--dark)', fontWeight: 600, fontFamily: 'var(--font-heading)' }}>{fmtTs(entry.timestamp)}</div>
                  <div style={{ fontSize: 11, color: 'var(--dark-40)', marginTop: 2 }}>by {entry.actor}</div>
                </div>
                <Badge bg={badge.bg} color={badge.color}>{entry.action}</Badge>
                <span style={{ fontSize: 14, color: 'var(--dark-60)' }}>{entry.details}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
